/**
 * Mikrofon-Spur — wer greift wann nach dem Mikrofon? (Ticket 570, 2026-07-30)
 *
 * Am 29.07. wurden 64 Aufnahme-Abrisse in 48 Stunden gemessen, alle mit
 * derselben Ursache: endReason=mikro-belegt:os — iOS gibt das Mikrofon nicht
 * her. Die naheliegende Frage war, WER es belegt: das Vorlesen, Push-to-Talk,
 * ein Anruf.
 *
 * Diese Frage liess sich nicht beantworten, und der Grund war eine Luecke,
 * nicht ein Fehler: Die entsprechenden Ereignisse werden nur in der Konsole
 * des Browsers geloggt und erreichen den Server nie. Eine Auswertung der
 * Server-Protokolle suchte also nach etwas, das dort nicht ankommen kann —
 * und haette beinahe "kein Zusammenhang" als Befund geliefert.
 *
 * Hier werden dieselben Momente an den Server gemeldet, auf demselben Weg,
 * den die Vorlese-Messung schon benutzt (/api/debug/ptt-timing). Damit zeigt
 * der naechste Abriss, was in den Sekunden davor passiert ist, statt uns
 * raten zu lassen.
 *
 * Bewusst sparsam: nur Zeitpunkt und Anlass, keine Inhalte. Und bewusst an
 * EINER Stelle — die Ereignisse an drei Orten einzeln zu melden waere genau
 * die Krankheit, die dieses Projekt schon oft genug hatte.
 */
import { mitAufnahmeKennung } from "./aufnahme-kennung.js";

/** Meldet einen Mikrofon-Anlass an den Server. Fehler werden geschluckt —
 *  eine Diagnose darf nie den Betrieb stoeren. */
export function meldeMikroAnlass(anlass: string, zusatz = ""): void {
  meldeSpur("mikro", `${anlass}${zusatz ? " " + zusatz : ""}`);
}

/**
 * Eine Zeile ins Server-Protokoll — für jeden Bereich, nicht nur das Mikro.
 * (23.09.2026)
 *
 * ⚠️ Anlass war Martins Frage, warum der Spiegel als angesehen galt, obwohl
 *    er ihn „hundertprozentig" nicht angesehen hatte. Das Protokoll zeigte
 *    sekundengenau DASS die Spiegel-Ansicht aufging — aber nicht WODURCH.
 *    Fünf Wege öffnen sie, und keiner sagte seinen Namen. Dieselbe Lehre
 *    wie beim Stopp der Tagesaufnahme (17.09.): jeder Aufruf nennt seinen
 *    Verursacher, dann ist der nächste Fall in Sekunden benannt.
 *
 * Der Kanal ist derselbe wie für das Mikro; er war nur fest auf „[mikro]"
 * verdrahtet. Ein zweiter Kanal daneben wäre eine zweite Wahrheit.
 */
export function meldeSpur(bereich: string, text: string): void {
  // Welle 0 (lib/aufnahme-kennung.ts): die Jev-Zeilen tragen die Kennung der Aufnahme und die Regelfassung.
  const line = mitAufnahmeKennung(`[${bereich}] ${text}`);
  try {
    console.log(line);
    void fetch("/api/debug/ptt-timing", {
      method: "POST",
      body: JSON.stringify({ line }),
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch {
    /* Diagnose ist nie wichtiger als die Aufnahme. */
  }
}

/**
 * Haengt sich an die Ereignisse, die es schon gibt. Einmal beim Start rufen.
 *
 * ⚠️ 2026-07-30, nach der ersten Messung korrigiert: Die beiden Ereignisse
 * haben VERSCHIEDENE KOERNUNG, und die erste Benennung hat das verschleiert.
 *
 *   tts-audio-start feuert pro STUECK — bei einer langen Antwort dutzende Mal.
 *   tts-audio-end feuert einmal pro ZUG, wenn die Warteschlange leerlaeuft,
 *     und nur wenn nicht abgebrochen wurde (siehe tts-queue.ts resolveIdle).
 *
 * Die erste Messung ergab 14 "vorlesen-start" gegen 1 "vorlesen-ende". Das
 * sieht nach einem Leck aus — als wuerde das Mikrofon 13 Mal nicht
 * freigegeben. Es ist aber richtig: 14 Stuecke in einem Zug, ein Zug fertig.
 * Wer das spaeter liest, ohne den Code zu kennen, jagt einem Fehler nach, den
 * es nicht gibt.
 *
 * Deshalb heissen sie jetzt so, wie sie sich verhalten. Zaehlen sich die
 * beiden nicht auf, ist das kein Befund.
 */
export function starteMikroSpur(): void {
  window.addEventListener("voice-v8:tts-audio-start", () => {
    // Pro Stueck. Fuer die Abriss-Diagnose genau richtig: es sagt, dass in
    // dieser Sekunde Ton lief.
    meldeMikroAnlass("vorlesen-stueck");
  });
  window.addEventListener("voice-v8:tts-audio-end", () => {
    // Pro Zug, und nur ohne Abbruch. Das ist der Moment, in dem das
    // Vorlesen das Mikrofon tatsaechlich wieder freigibt.
    meldeMikroAnlass("vorlesen-zug-fertig");
  });
}
